import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { FUNDAMENTALS_BACKING_MANIFEST } from "../generated/fundamentals-audio";
import type { ContentPayload } from "../types";
import { completedModules, markComplete } from "../services/progress";
import "../components/kit-explorer";
import "../components/sound-compare";
import "../components/groove-lab";

@customElement("learn-page")
export class LearnPage extends LitElement {
  @property({ attribute: false }) content!: ContentPayload;
  @property() slug = "";
  @state() private answer?: number;
  @state() private completed = false;
  protected createRenderRoot() { return this; }

  willUpdate(changed: Map<PropertyKey, unknown>): void {
    if (changed.has("slug")) {
      this.answer = undefined;
      this.completed = completedModules().has(this.slug);
    }
  }

  render() {
    const module = this.content.course.modules.find((candidate) => candidate.slug === this.slug);
    if (!module) return html`<main class="empty-state"><h1>Lesson not found</h1><a href="/">Return home</a></main>`;
    const index = this.content.course.modules.indexOf(module);
    const previous = this.content.course.modules[index - 1];
    const next = this.content.course.modules[index + 1];
    const metalKitManifest = this.content.catalog.studies
      .find((study) => study.slug === "heaviest-matter-of-the-universe")
      ?.audio?.manifests?.["1"];
    return html`
      <main class="lesson-page">
        <nav class="lesson-rail" aria-label="Fundamentals">
          ${this.content.course.modules.map((item) => html`<a class=${item.slug === module.slug ? "active" : ""} href="/learn/${item.slug}"><span>0${item.number}</span>${item.title}</a>`)}
        </nav>
        <article class="lesson-content">
          <header class="lesson-header">
            <div class="eyebrow">Fundamentals / 0${module.number} · ${module.duration}</div>
            <h1>${module.title}</h1>
            <p class="lede">${module.summary}</p>
          </header>
          <section class="concept-panel count-${module.concepts.length}">
            ${module.concepts.map((concept, conceptIndex) => html`<div><span>0${conceptIndex + 1}</span><p>${concept}</p></div>`)}
          </section>

          ${module.number === 1 ? html`
            <section class="interactive-block">
              <div class="block-heading"><span class="eyebrow">See it · name it · hear it</span><h2>A real kit, then the metal expansion</h2></div>
              <kit-explorer></kit-explorer>
            </section>
          ` : ""}

          ${module.number === 2 ? html`
            <section class="interactive-block groove-block">
              <div class="block-heading"><span class="eyebrow">One change · two sounds</span><h2>Wood shell vs steel shell</h2></div>
              <sound-compare></sound-compare>
            </section>
          ` : ""}

          ${module.number >= 3 ? html`
            <section class="interactive-block groove-block">
              <div class="block-heading"><span class="eyebrow">Change one thing</span><h2>${module.number === 3 ? "Build the foundation" : module.number === 4 ? "Same tempo, different weight" : module.number === 5 ? "Make the groove breathe" : "See how the engines work"}</h2></div>
              <groove-lab
                concept=${module.slug}
                .kitManifest=${metalKitManifest ?? ""}
                .backingManifest=${FUNDAMENTALS_BACKING_MANIFEST}
              ></groove-lab>
            </section>
          ` : ""}

          ${module.number === 6 ? this.renderGraphVocabulary() : ""}

          <section class="ear-check">
            <span class="eyebrow">Optional ear check</span>
            <h2>${module.check.question}</h2>
            <div class="answer-grid">
              ${module.check.answers.map((answer, answerIndex) => html`<button class=${this.answer === answerIndex ? "selected" : ""} @click=${() => { this.answer = answerIndex; }}>${answer}</button>`)}
            </div>
            ${this.answer !== undefined ? html`<p class="answer ${this.answer === module.check.correct ? "correct" : "try"}">${this.answer === module.check.correct ? "Yes. " : "Listen again. "}${module.check.explanation}</p>` : ""}
          </section>

          <footer class="lesson-footer">
            ${previous ? html`<a class="button" href="/learn/${previous.slug}">← ${previous.title}</a>` : html`<a class="button" href="/">← Course home</a>`}
            <button class="button primary" @click=${() => { markComplete(module.slug); this.completed = true; }}>${this.completed ? "Completed ✓" : "Mark complete"}</button>
            ${next ? html`<a class="button" href="/learn/${next.slug}">${next.title} →</a>` : html`<a class="button" href="/graph">Explore graph →</a>`}
          </footer>
        </article>
      </main>
    `;
  }

  private renderGraphVocabulary() {
    const terms = [
      ["Pocket & weight", "How a few carefully placed hits make a riff feel deep and heavy."],
      ["Swing & fills", "An uneven long–short pulse, plus short departures that lead into a new phrase."],
      ["Footwork & gallop", "Patterns led by one or both feet, including the long–short–short gallop."],
      ["Punk / thrash drive", "D-beats and skank beats that make the music lean forward."],
      ["Blast architecture", "Different ways kick, snare, and cymbal can alternate or land together at speed."],
      ["Riff-synchronous groove", "Kick and accents that copy the guitar rhythm."],
      ["Odd grouping", "A short pattern repeats across the bar while a familiar backbeat keeps your place."],
      ["Texture & dynamics", "Soft and strong hits, plus cymbal and tom choices that change the feel without changing the beat."],
    ];
    return html`<section class="vocabulary-bridge"><div class="block-heading"><span class="eyebrow">Ready for the graph</span><h2>Eight labels you already know by ear</h2><p>These are the graph's filters. Each is a plain name for something you have heard or changed in the lessons.</p></div><div class="vocabulary-grid">${terms.map(([term, explanation]) => html`<div><b>${term}</b><p>${explanation}</p></div>`)}</div></section>`;
  }
}
